// Duplicate review before an import (every business, every bank).
// The server already skips rows that are exactly in the account (same date,
// amount and text, or the same bank reference). This step also shows
// "possible" duplicates (same amount and direction within a few days, other
// text) so the person decides; nothing is dropped without their choice.
import api from '../../services/api';

// Rows per check request. Large files are checked in batches so the check
// never times out; indexes in the result always refer to the full list.
export const CHECK_BATCH = 200;

export async function findDuplicates(accountId, rows) {
  const all = [];
  for (let start = 0; start < (rows || []).length; start += CHECK_BATCH) {
    // eslint-disable-next-line no-await-in-loop
    const found = await checkBatch(accountId, rows.slice(start, start + CHECK_BATCH));
    for (const d of found) all.push({ ...d, index: d.index + start });
  }
  return all;
}

async function checkBatch(accountId, rows) {
  const body = {
    account_id: accountId,
    transactions: rows.map((t) => ({
      transaction_date: t.transaction_date,
      description: t.description,
      amount: t.amount,
      direction: t.direction || null,
      reference_number: t.reference_number ?? null,
      transaction_type: t.transaction_type,
      payee: t.payee ?? null,
    })),
  };
  const response = await api.post('/transactions/check-duplicates', body);
  return Array.isArray(response?.data?.duplicates) ? response.data.duplicates : [];
}

// Default choice per duplicate: exact ones are skipped (the server would skip
// them anyway); same-text ones (same amount and description within a few
// days, a re-import with a shifted date) and possible ones start as "skip" so
// a re-import never doubles rows silently; the person can switch any
// non-exact one to "import".
export function defaultDecisions(duplicates) {
  const out = {};
  for (const d of duplicates || []) out[d.index] = 'ignore';
  return out;
}

// Rows to send: everything except the ones the person chose to skip.
export function rowsToImport(rows, decisions) {
  return (rows || []).filter((_, i) => decisions?.[i] !== 'ignore');
}

// Marks preview rows with what the duplicate check found ('exact' or
// 'possible'); indexes refer to the rows that were sent.
export function markDuplicates(rows, duplicates) {
  for (const d of duplicates || []) {
    if (rows[d.index]) rows[d.index]._duplicate = d.match === 'exact' ? 'exact' : 'possible';
  }
  return rows;
}

export function duplicateCounts(rows) {
  const exact = (rows || []).filter((r) => r._duplicate === 'exact').length;
  const possible = (rows || []).filter((r) => r._duplicate === 'possible').length;
  return { exact, possible, total: exact + possible };
}
